/*
 * ANIME CRUSH — TRAILER MODAL
 *
 * "Watch Trailer" used to be an outbound link: one click and the customer was
 * on YouTube, off the storefront, with the page they were reading abandoned
 * behind them. This keeps them here.
 *
 * WHAT IT GUARANTEES
 *   - the iframe does not exist until the modal opens, so no page pays for an
 *     embed it never shows, and there is no empty bordered box anywhere
 *   - youtube-nocookie.com, so no tracking cookie is set by simply loading a
 *     page that happens to have a trailer button on it
 *   - the scrollbar is compensated when the body locks, because the classic
 *     way a modal "jumps" the page is removing a 15px scrollbar and letting
 *     everything shift right
 *   - focus moves into the dialog, is trapped there, and returns to the button
 *     that opened it
 *   - ESC, the close button and the backdrop all close it
 *   - it is inert to screen readers when closed
 *
 * USAGE. Any element with data-ac-trailer becomes a trigger:
 *
 *   <button data-ac-trailer="QBlPg818lGI"
 *           data-ac-trailer-title="Cyberpunk: Edgerunners 2"
 *           data-ac-trailer-note="Official teaser - Netflix">Watch Trailer</button>
 *
 * The id is the ONLY thing that varies. A trigger with no id does nothing,
 * which is why callers are expected to not render the button at all when no
 * official trailer could be verified.
 */
(function () {
  'use strict';

  var modal, frameHost, titleEl, noteEl, ytLink, closeBtn;
  var lastFocus = null;
  var isOpen = false;

  function el(id) { return document.getElementById(id); }

  function cacheNodes() {
    modal = el('ac-trailer-modal');
    if (!modal) return false;
    frameHost = modal.querySelector('[data-ac-trailer-frame]');
    titleEl = modal.querySelector('[data-ac-trailer-heading]');
    noteEl = modal.querySelector('[data-ac-trailer-note]');
    ytLink = modal.querySelector('[data-ac-trailer-yt]');
    closeBtn = modal.querySelector('[data-ac-trailer-close]');
    return true;
  }

  /* The scrollbar width, measured rather than assumed - it is 0 on overlay
   * scrollbar platforms and ~15px on Windows. */
  function scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function focusables() {
    return Array.prototype.filter.call(
      modal.querySelectorAll('a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null || n.tagName === 'IFRAME'; });
  }

  function onKey(e) {
    if (!isOpen) return;
    if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab' && e.keyCode !== 9) return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open(trigger) {
    if (!modal && !cacheNodes()) return;
    var id = trigger.getAttribute('data-ac-trailer');
    if (!id) return;

    lastFocus = trigger;

    titleEl.textContent = trigger.getAttribute('data-ac-trailer-title') || 'Trailer';
    var note = trigger.getAttribute('data-ac-trailer-note') || '';
    noteEl.textContent = note;
    noteEl.hidden = !note;
    ytLink.href = 'https://www.youtube.com/watch?v=' + encodeURIComponent(id);

    /* Built here, not in the markup: nothing loads until this moment. */
    var iframe = document.createElement('iframe');
    iframe.setAttribute('title', titleEl.textContent + ' trailer');
    iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'eager');
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
                 '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    frameHost.innerHTML = '';
    frameHost.appendChild(iframe);

    var sb = scrollbarWidth();
    document.body.style.overflow = 'hidden';
    if (sb > 0) document.body.style.paddingRight = sb + 'px';

    modal.hidden = false;
    modal.removeAttribute('aria-hidden');
    /* next frame, so the transition has something to transition from */
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    isOpen = true;

    (closeBtn || modal).focus();
    document.addEventListener('keydown', onKey, true);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    modal.classList.remove('is-open');
    document.removeEventListener('keydown', onKey, true);

    /* Kill the embed immediately so audio stops the moment it is dismissed,
     * then hide after the transition rather than before it. */
    frameHost.innerHTML = '';

    document.body.style.overflow = '';
    document.body.style.paddingRight = '';

    window.setTimeout(function () {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    }, 220);

    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-ac-trailer]') : null;
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    if (!isOpen || !modal) return;
    if (e.target.closest('[data-ac-trailer-close]') || e.target === modal ||
        e.target.hasAttribute('data-ac-trailer-backdrop')) {
      e.preventDefault();
      close();
    }
  });

  /*
   * ARTICLES KEEP THEIR OWN TRAILER BLOCK; ONLY THE BEHAVIOUR CHANGES.
   *
   * News articles ship a .ac-trailer block whose embed is display:none and
   * whose visible control is a plain link to youtube.com with target=_blank -
   * so "Watch Trailer" in an article threw the reader off the site, which is
   * the thing this modal exists to stop.
   *
   * The article HTML is not rewritten. The link is upgraded in place into a
   * modal trigger, and with JavaScript off it stays exactly what it was: a
   * working link to the trailer.
   */
  function parseVideoId(href) {
    var s = String(href || '');
    var m = s.match(/[?&]v=([\w-]{6,})/) ||
            s.match(/youtu\.be\/([\w-]{6,})/) ||
            s.match(/embed\/([\w-]{6,})/);
    return m ? m[1] : null;
  }

  function upgradeArticleTrailers() {
    var scope = document.querySelector('.NewsArticle__Body');
    if (!scope) return;
    var t = document.querySelector('.NewsArticle__Title');
    var title = t ? t.textContent.trim() : document.title;
    var links = scope.querySelectorAll('a[href*="youtube.com/watch"], a[href*="youtu.be/"]');
    Array.prototype.forEach.call(links, function (a) {
      var id = parseVideoId(a.getAttribute('href'));
      if (!id || a.hasAttribute('data-ac-trailer')) return;
      a.setAttribute('data-ac-trailer', id);
      a.setAttribute('data-ac-trailer-title', title);
      a.setAttribute('data-ac-trailer-note', 'Official trailer');
      a.removeAttribute('target');
    });
  }

  function boot() { cacheNodes(); upgradeArticleTrailers(); }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
